import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, IconButton, FormGroup, TextField, Autocomplete, Switch, CircularProgress, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { ArrowBack, ArrowForwardIos } from '@mui/icons-material';
import moment from 'moment';
import { useIntl } from 'react-intl';
import messages from '../messages';

import { getStyles } from './styles';
import { fetchAccountId, getUserName } from '../../../../utils/GetAccountDetails';
import { tostAlert } from '../../../../utils/AlertToast';
import { apiErrorHandler } from '../../../../utils/ApiErrorHandler';
import LoadBar from '../../../../utils/LoadBar';
import { useAppContext } from '../../../../context/AppContext';
import { IconSvg } from '../../../../utils/globalIcons';
import { ReactSVG } from 'react-svg';
import { getComponentsStyles } from '../../../../styles/componentsStyles';
import AnalyticsApi from '../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import { setAnalyticsDataSourcesAdded } from '../../../../Redux/slice/newDataSlice';
import AnalyticsDataSourcesApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSources';

function AddIntegrations({ setSelectedMenuItem }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const componentsStyles = getComponentsStyles(theme);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();

    const connection_type = location?.state?.connection_type ?? null;
    const connection_label = location?.state?.connection_label ?? '';
    const action_type = location?.state?.action_type ?? '';
    const connector_id = location?.state?.connector_id ?? null;
    const all_projects = useSelector(state => state.all_data.projects);

    const { selectedOrganization, selectedWorkspace, selectedProject } = useAppContext();

    const [connectionName, setConnectionName] = useState('')

    const s3_Details = { access_key: '', secret_access_key: '', endpoint_url: '' }
    const [s3Data, setS3Data] = useState(s3_Details)

    const mongoDbDetails = { connectionStatus: '', connection_mongo_uri: '', mongodbs: [], connection_mongo_db: null }
    const [mongoDbData, setMongoDbData] = useState(mongoDbDetails)
    const [extractionProjects, setExtractionProjects] = useState([])
    const [appflyteProjects, setAppflyteProjects] = useState([])
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [selectedProjects, setSelectedProjects] = useState([])
    const [datasetPurpose, setDatasetPurpose] = useState('appflyte_query')

    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (!connection_type) {
            navigate('/connectors/extraction-agent/connector-lists')
            return
        }
        if (connection_type === "ameya_extract") {
            setMongoDbData({
                ...mongoDbData,
                connection_mongo_uri: window.__ENV__.MONGODB_URI,
                mongodbs: [window.__ENV__.MONGO_DB_DATABASE],
                connection_mongo_db: window.__ENV__.MONGO_DB_DATABASE
            })
            getAllProject()
        }
        if (connection_type === "appflyte") {
            getAllAppflyteProject()
        }
    }, [connection_type])

    useEffect(() => {
        const errors = { ...formErrors };
        if (formErrors?.connectionName && connectionName?.trim()) { delete errors?.connectionName }

        if (formErrors?.access_key && s3Data?.access_key?.trim()) { delete errors?.access_key }
        if (formErrors?.secret_access_key && s3Data?.secret_access_key?.trim()) { delete errors?.secret_access_key }
        if (formErrors?.endpoint_url && s3Data?.endpoint_url?.trim()) { delete errors?.endpoint_url }

        if (formErrors?.connection_mongo_uri && mongoDbData?.connection_mongo_uri?.trim()) { delete errors?.connection_mongo_uri }
        if (formErrors?.connection_mongo_db && mongoDbData?.connection_mongo_db) { delete errors?.connection_mongo_db }

        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) { setFormErrors(errors) }
    }, [connectionName, s3Data, mongoDbData, formErrors]);


    function getOrganizationCollection(list, prefix) {
        return list.filter(item => item.startsWith(prefix + "_")).map(item => item.split("_")[1]);
    }

    function filterItemsByAutoId(items, idList) {
        const idSet = new Set(idList);
        return items.filter(item => idSet.has(item?.payload?.__auto_id__));
    }

    const getAllProject = async () => {
        setSettingsLoading(true)
        try {
            const response = await fetchMongoCollections();
            const filtered_collections = getOrganizationCollection(response, selectedOrganization?.payload?.__auto_id__);
            const filtered_projects = filterItemsByAutoId(all_projects, filtered_collections);
            setExtractionProjects(filtered_projects)
        } catch (error) {
            console.log(error)
        } finally {
            setSettingsLoading(false)
        }
    }

    const getAllAppflyteProject = async () => {
        setSettingsLoading(true)
        try {
            const filtered_projects = (all_projects || [])?.filter(item => item?.payload?.configuration?.engine_name === "appflyte_agent")
            setAppflyteProjects(filtered_projects)
        } catch (error) {
            console.log(error)
        } finally {
            setSettingsLoading(false)
        }
    }

    // const handleSwitchChange = (item) => {
    //     setSelectedProjects((prevSelected) => {
    //         const isSelected = prevSelected.some((project) => project?.payload?.__auto_id__ === item?.payload?.__auto_id__);
    //         if (isSelected) {
    //             return prevSelected.filter((project) => project?.payload?.__auto_id__ !== item?.payload?.__auto_id__);
    //         } else {
    //             return [...prevSelected, item];
    //         }
    //     });
    // };

    const handleSwitchChange = (item) => {
        setSelectedProjects([item]);
    };

    const handleTestConnection = async () => {
        setLoading(true)
        try {
            const errors = {};
            if (!mongoDbData?.connection_mongo_uri?.trim()) { errors.connection_mongo_uri = 'This field is required' }

            if (Object.keys(errors).length === 0) {

                setMongoDbData({ ...mongoDbData, mongodbs: [], connectionStatus: "" })
                const reqObj = { "uri": mongoDbData.connection_mongo_uri }
                const response = await AnalyticsApi.mongoConnectionDBLists(JSON.stringify(reqObj));

                if (response.status === 200) {
                    const connectionDbs = response?.data?.dbs ?? [];
                    const connectionURI = response?.data?.uri ?? "";
                    setMongoDbData({ ...mongoDbData, connectionStatus: "success", mongodbs: connectionDbs, connection_mongo_uri: connectionURI })
                } else {
                    setMongoDbData({ ...mongoDbData, connectionStatus: "failed" })
                }
            } else {
                setFormErrors(errors);
            }
        } catch (error) {
            console.log(error);
            setMongoDbData({ ...mongoDbData, connectionStatus: "failed" })
            tostAlert(intl.formatMessage(messages.connection_error), 'error');
        } finally {
            setLoading(false)
        }
    }

    const handlePurposeChange = (item) => {
        setDatasetPurpose(item);
    };

    const fetchMongoCollections = async () => {
        try {
            const reqObj = {
                "uri": window.__ENV__.MONGODB_URI,
                "db": window.__ENV__.MONGO_DB_DATABASE
            }
            const response = await AnalyticsApi.mongoConnectionCollectionsLists(JSON.stringify(reqObj));
            if (response.status === 200) {
                const responseData = response?.data ?? {};
                const collections = responseData?.collections ?? [];
                return collections;
            }
        } catch (error) {
            console.log(error)
            return [];
        }
    }

    const handleCreateMongoIndex = async (source_id) => {
        try {
            const reqObj = { "source_id": source_id }
            const response = await AnalyticsApi.createMongoIndex(JSON.stringify(reqObj));
            if (response.status === 200) {
                const job_id = response.data?.job_id ?? null;
                return job_id
            }
            return null
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const handleCreateMongoFieldIndex = async (source_id) => {
        try {
            const reqObj = { "source_id": source_id }
            const response = await AnalyticsApi.createMongoFieldIndex(JSON.stringify(reqObj));
            if (response.status === 200) {
                const job_id = response.data?.job_id ?? null;
                return job_id
            }
            return null
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const handleMongoRegister = async (collections) => {
        try {

            const new_source_id = uuid();

            const reqObj = {
                "connection_details": {
                    "uri": mongoDbData.connection_mongo_uri,
                    "db": mongoDbData?.connection_mongo_db,
                    "collection": collections
                },
                "source_details": {
                    "source_name": connectionName,
                    "source_id": new_source_id
                }
            }

            const response = await AnalyticsApi.mongoUserConnectionRegister(JSON.stringify(reqObj));
            if (response.status !== 200) {
                return false
            }
            const responseData = response.data ?? {};
            const source_id = responseData?.source_id ?? null;
            const [mongoIndexJobId, mongoFieldIndexJobId] = await Promise.all([
                handleCreateMongoIndex(source_id),
                handleCreateMongoFieldIndex(source_id)
            ])
            if (!mongoIndexJobId || !mongoFieldIndexJobId) {
                return { mongoIndexJobId: null, mongoFieldIndexJobId: null, source_id: null }
            } else {
                return { mongoIndexJobId: mongoIndexJobId, mongoFieldIndexJobId: mongoFieldIndexJobId, source_id: source_id }
            }
        } catch (error) {
            console.log(error)
            return { mongoIndexJobId: null, mongoFieldIndexJobId: null, source_id: null }
        }
    }

    const handleSave = async () => {
        setLoading(true)
        try {

            const errors = {};
            if (!connectionName?.trim()) { errors.connectionName = 'This Field is required' }

            if (connection_type === "aws_s3") {
                if (!s3Data.access_key?.trim()) { errors.access_key = 'This Field is required' }
                if (!s3Data.secret_access_key?.trim()) { errors.secret_access_key = 'This Field is required' }
                if (!s3Data.endpoint_url?.trim()) { errors.endpoint_url = 'This Field is required' }
            }

            if (connection_type === "mongodb") {
                if (!mongoDbData.connection_mongo_uri?.trim()) { errors.connection_mongo_uri = 'This Field is required' }
                if (!mongoDbData?.connection_mongo_db) { errors.connection_mongo_db = 'This field is required' }
            }

            if (connection_type === "ameya_extract" && (selectedProjects || [])?.length === 0) {
                tostAlert(intl.formatMessage(messages.project_check), 'warning')
                return
            }

            if (connection_type === "appflyte" && (selectedProjects || [])?.length === 0) {
                tostAlert(intl.formatMessage(messages.project_check), 'warning')
                return
            }

            if (Object.keys(errors).length === 0) {
                const userName = getUserName();

                const collection_item = {
                    name: connectionName,
                    workspace_id: selectedWorkspace?.payload?.__auto_id__ ?? null,
                    project_id: selectedProject?.payload?.__auto_id__ ?? null,
                    organization_id: selectedOrganization?.payload?.__auto_id__ ?? null,
                    created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                    created_by: userName
                };

                if (connection_type === 'aws_s3') {
                    collection_item.source_type = 'aws_s3';
                    collection_item.access_key = s3Data.access_key;
                    collection_item.secret_access_key = s3Data.secret_access_key;
                    collection_item.endpoint_url = s3Data.endpoint_url;
                    collection_item.status = 'connected';
                }

                if (connection_type === 'mongodb') {
                    const mongo_collections = []
                    const { mongoIndexJobId, mongoFieldIndexJobId, source_id } = await handleMongoRegister(mongo_collections);
                    if (!mongoIndexJobId || !mongoFieldIndexJobId || !source_id) {
                        tostAlert(intl.formatMessage(messages.integration_error), 'error')
                        return
                    }
                    collection_item.source_type = 'mongodb';
                    collection_item.connection_url = mongoDbData.connection_mongo_uri;
                    collection_item.mongoIndexJobId = mongoIndexJobId;
                    collection_item.mongoFieldIndexJobId = mongoFieldIndexJobId;
                    collection_item.status = 'in_progress';
                    collection_item.database = mongoDbData?.connection_mongo_db;
                    collection_item.__auto_id__ = source_id;
                }

                if (connection_type === 'ameya_extract') {

                    const organization_id = selectedOrganization?.payload?.__auto_id__ ?? null;
                    const matchedCollections = selectedProjects.map(p => `${organization_id}_${p.payload?.__auto_id__}`);
                    const extraction_projects = selectedProjects.map(p => p.payload?.__auto_id__);

                    if ((matchedCollections || [])?.length === 0) {
                        tostAlert(intl.formatMessage(messages.integration_data_error), 'error')
                        return
                    }

                    const { mongoIndexJobId, mongoFieldIndexJobId, source_id } = await handleMongoRegister(matchedCollections);
                    if (!mongoIndexJobId || !mongoFieldIndexJobId || !source_id) {
                        tostAlert(intl.formatMessage(messages.integration_error), 'error')
                        return
                    }
                    collection_item.source_type = 'ameya_extract';
                    collection_item.connection_url = mongoDbData.connection_mongo_uri;
                    collection_item.mongoIndexJobId = mongoIndexJobId;
                    collection_item.mongoFieldIndexJobId = mongoFieldIndexJobId;
                    collection_item.status = 'in_progress';
                    collection_item.database = mongoDbData?.connection_mongo_db;
                    collection_item.extraction_projects = extraction_projects;
                    collection_item.__auto_id__ = source_id;
                }

                if (connection_type === 'appflyte') {

                    const account_id = await fetchAccountId();
                    const appflyte_schemas = selectedProjects.map(p => p.payload?.__auto_id__);
                    const default_schema_id = window.__ENV__.AMEYA_COLLECTIONS_SCHEMA_ID

                    if (datasetPurpose === 'appflyte_query' && default_schema_id) {
                        appflyte_schemas.push(default_schema_id);
                    }

                    const reqObj = {
                        "workspace_id": selectedWorkspace?.payload?.__auto_id__ ?? null,
                        "project_id": selectedProject?.payload?.__auto_id__ ?? null,
                        "schema_ids": appflyte_schemas,
                        "account_id": account_id
                    }

                    const mcp_response = await AnalyticsApi.createAppflyteSource(JSON.stringify(reqObj))
                    if (mcp_response.status !== 200) {
                        tostAlert(intl.formatMessage(messages.integration_error), 'error')
                        return
                    }

                    const new_source_id = uuid();
                    collection_item.source_type = 'appflyte';
                    collection_item.status = 'connected';
                    collection_item.appflyte_schemas = appflyte_schemas;
                    collection_item.purpose = datasetPurpose;
                    collection_item.__auto_id__ = new_source_id;
                }

                const settingsObject = { collection_item };
                const create_response = await AnalyticsDataSourcesApi.addData(JSON.stringify(settingsObject));
                if (create_response.status === 200) {
                    tostAlert(intl.formatMessage(messages.connection_success), 'success')
                    dispatch(setAnalyticsDataSourcesAdded(true))
                    navigate('/sources/analytics-tool/integrations')
                    setSelectedMenuItem(1)
                } else {
                    tostAlert(intl.formatMessage(messages.connection_error), 'error')
                }

            } else {
                setFormErrors(errors);
                return
            }

        } catch (error) {
            console.log(error)
            apiErrorHandler(error)
        } finally {
            setLoading(false)
        }
    }

    const connectorNames = {
        aws_s3: "AWS S3",
        azure_blob: "Azure Blob",
        mongodb: "Mongo DB"
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {loading && <LoadBar />}

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Box display={'flex'} alignItems={'center'}>
                            <IconButton onClick={() => navigate('/sources/analytics-tool/connectors')}>
                                <ArrowBack sx={{ color: '#0B51C5' }} />
                            </IconButton>
                            <Typography
                                onClick={() => navigate('/sources/analytics-tool/connectors')}
                                sx={{ ...styles.mainHeadingText, '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                            >
                                All Connectors
                            </Typography>
                            <ArrowForwardIos sx={{ fontSize: 16, ml: 1, mr: 1 }} />
                            <Typography sx={styles.mainHeadingText}>
                                {connectorNames[connection_label] || connection_label}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            onClick={handleSave}
                            disabled={loading || settingsLoading}
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ ...styles.btnText, color: '#FFFFFF' }}>SAVE</Typography>
                        </Button>
                        <Button
                            onClick={() => navigate('/sources/analytics-tool/connectors')}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ ...styles.btnText, color: '#000000' }}>CANCEL</Typography>
                        </Button>
                    </Box>
                </Box>

                <Box mb={2} mt={2}>
                    <Typography mb={1}>Connection Name <span style={{ color: 'red' }}>*</span></Typography>
                    <TextField
                        variant="outlined"
                        type="text"
                        size='small'
                        sx={{ width: '40%' }}
                        value={connectionName}
                        onChange={(e) => setConnectionName(e.target.value)}
                    />
                    {formErrors.connectionName && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.connectionName}</Typography>}
                </Box>

                {connection_type === "aws_s3" &&
                    (<Box mb={2}>
                        <FormGroup>
                            <Box mb={2}>
                                <Typography mb={1}>Access Key <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={s3Data.access_key}
                                    onChange={(e) => setS3Data({ ...s3Data, access_key: e.target.value })}
                                />
                                {formErrors.access_key && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.access_key}</Typography>}
                            </Box>

                            <Box mb={2}>
                                <Typography mb={1}>Secret Access Key <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={s3Data.secret_access_key}
                                    onChange={(e) => setS3Data({ ...s3Data, secret_access_key: e.target.value })}
                                />
                                {formErrors.secret_access_key && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.secret_access_key}</Typography>}
                            </Box>

                            <Box>
                                <Typography mb={1}>Endpoint URL <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='small'
                                    sx={{ width: '600px' }}
                                    value={s3Data.endpoint_url}
                                    onChange={(e) => setS3Data({ ...s3Data, endpoint_url: e.target.value })}
                                />
                                {formErrors.endpoint_url && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.endpoint_url}</Typography>}
                            </Box>

                        </FormGroup>
                    </Box>)
                }

                {connection_type === "azure_blob" &&
                    (<Box>
                        {/* azure_blob */}
                    </Box>)
                }

                {connection_type === "mongodb" &&
                    (<Box mt={2} mb={2}>
                        <FormGroup>
                            <Box mb={1}>
                                <Typography mb={1}>Connection String <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    variant="outlined"
                                    type="text"
                                    size='medium'
                                    sx={{ width: '600px' }}
                                    value={mongoDbData.connection_mongo_uri}
                                    onChange={(e) => setMongoDbData({ ...mongoDbData, connection_mongo_uri: e.target.value })}
                                />
                                {formErrors.connection_mongo_uri && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.connection_mongo_uri}</Typography>}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '50px', marginBottom: 2 }}>

                                <Typography
                                    sx={{ fontSize: '14px', fontWeight: '600', color: '#0B51C5', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    onClick={() => { !loading && handleTestConnection() }}
                                >
                                    TEST CONNECTION
                                </Typography>

                                {mongoDbData.connectionStatus === "success" &&
                                    (<Box display={'flex'} alignItems={'center'} flexDirection={'row'} gap={'7px'}>
                                        <ReactSVG
                                            src={IconSvg.plugConnectedIcon}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                                svg.setAttribute('stroke', '#1BA559');
                                            }}
                                        />
                                        <Typography sx={styles.paraText}>Connection successful</Typography>
                                    </Box>)
                                }
                                {mongoDbData.connectionStatus === "failed" &&
                                    (<Box display={'flex'} alignItems={'center'} flexDirection={'row'} gap={'7px'}>
                                        <ReactSVG
                                            src={IconSvg.plugNotConnectedIcon}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                                svg.setAttribute('stroke', '#E00000');
                                            }}
                                        />
                                        <Typography sx={styles.paraText}>Unable to connect</Typography>
                                    </Box>)
                                }
                            </Box>

                            <Box mb={2}>
                                <Typography sx={{ ...styles.paraText, marginBottom: '6px' }}>Select Database</Typography>
                                <Autocomplete
                                    sx={{ ...componentsStyles.autocomplete, width: '200px' }}
                                    options={mongoDbData.mongodbs}
                                    getOptionLabel={(option) => option || ""}
                                    id="clear-on-escape"
                                    clearOnEscape
                                    size='small'
                                    disabled={loading}
                                    value={mongoDbData.mongodbs.find((option) => option === mongoDbData.connection_mongo_db) || null}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined" placeholder="Database" />
                                    )}
                                    onChange={(event, value) => setMongoDbData({ ...mongoDbData, connection_mongo_db: value || null })}
                                />
                                {formErrors.connection_mongo_db && <Typography sx={{ ...styles.paraText, color: 'red' }}>{formErrors.connection_mongo_db}</Typography>}
                            </Box>

                        </FormGroup>
                    </Box>)
                }

                {connection_type === "ameya_extract" &&
                    (<Box mt={2} pb={2}>
                        <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>Select Project <span style={{ color: 'red' }}>*</span></Typography>
                        <Box marginTop={'10px'}>
                            {settingsLoading ?
                                (<Box sx={{ textAlign: 'center', marginTop: '50px' }}>
                                    <CircularProgress sx={{ color: '#0B51C5' }} />
                                </Box>)
                                :
                                (extractionProjects || [])?.length > 0 ? (extractionProjects?.map(item => (
                                    <Box sx={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px',
                                        width: '40%', border: '1px solid #D9D9D9', borderRadius: '10px', marginBottom: '20px'
                                    }}>
                                        <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
                                            {item?.payload?.name}
                                        </Typography>
                                        <Switch
                                            checked={selectedProjects?.some((project) => project?.payload?.__auto_id__ === item?.payload?.__auto_id__)}
                                            onChange={() => handleSwitchChange(item)}
                                        />
                                    </Box>
                                )))
                                    :
                                    (<Typography sx={{ textAlign: 'center', marginTop: '50px' }}>No Projects Available</Typography>)
                            }
                        </Box>
                    </Box>)
                }

                {connection_type === "appflyte" &&
                    (<Box mt={2} pb={2}>

                        <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>Select Purpose <span style={{ color: 'red' }}>*</span></Typography>
                        <Box marginTop={'10px'}>
                            <RadioGroup
                                aria-labelledby="bot-type-selection"
                                value={datasetPurpose}
                                onChange={(e) => handlePurposeChange(e.target.value)}
                            >
                                <Box
                                    sx={{
                                        bgcolor: "#FFFFFF", borderRadius: "5px", border: "1px solid #DEDEDE", cursor: "pointer",
                                        width: "40%", p: 1.5, mb: 1, "&:hover": { borderColor: "#59A292" },
                                    }}
                                >
                                    <FormControlLabel
                                        value="appflyte_query"
                                        control={<Radio sx={{ color: "#59A292", "&.Mui-checked": { color: "#59A292" } }} />}
                                        label={
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={{ fontWeight: "400", fontSize: "14px" }}>Read</Typography>
                                                <Typography sx={{ fontWeight: "400", fontSize: "12px" }}>
                                                    Only Read
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        bgcolor: "#FFFFFF", borderRadius: "5px", border: "1px solid #DEDEDE", cursor: "pointer",
                                        width: "40%", p: 1.5, mb: 1, "&:hover": { borderColor: "#59A292" },
                                    }}
                                >
                                    <FormControlLabel
                                        value="appflyte_ddl"
                                        control={<Radio sx={{ color: "#59A292", "&.Mui-checked": { color: "#59A292" } }} />}
                                        label={
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={{ fontWeight: "400", fontSize: "14px" }}>DDL</Typography>
                                                <Typography sx={{ fontWeight: "400", fontSize: "12px" }}>
                                                    All DDL Operations
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        bgcolor: "#FFFFFF", borderRadius: "5px", border: "1px solid #DEDEDE", cursor: "pointer",
                                        width: "40%", p: 1.5, mb: 1, "&:hover": { borderColor: "#59A292" },
                                    }}
                                >
                                    <FormControlLabel
                                        value="appflyte_dml"
                                        control={<Radio sx={{ color: "#59A292", "&.Mui-checked": { color: "#59A292" } }} />}
                                        label={
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={{ fontWeight: "400", fontSize: "14px" }}>DML</Typography>
                                                <Typography sx={{ fontWeight: "400", fontSize: "12px" }}>
                                                    All DML Operations
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </RadioGroup>
                        </Box>

                        <Typography sx={{ fontSize: "14px", fontWeight: "400", marginTop: '20px' }}>Select Project <span style={{ color: 'red' }}>*</span></Typography>
                        <Box marginTop={'10px'}>
                            {settingsLoading ?
                                (<Box sx={{ textAlign: 'center', marginTop: '50px' }}>
                                    <CircularProgress sx={{ color: '#0B51C5' }} />
                                </Box>)
                                :
                                (appflyteProjects || [])?.length > 0 ? (appflyteProjects?.map(item => (
                                    <Box sx={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px',
                                        width: '40%', border: '1px solid #D9D9D9', borderRadius: '10px', marginBottom: '20px'
                                    }}>
                                        <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
                                            {item?.payload?.name}
                                        </Typography>
                                        <Switch
                                            checked={selectedProjects?.some((project) => project?.payload?.__auto_id__ === item?.payload?.__auto_id__)}
                                            onChange={() => handleSwitchChange(item)}
                                        />
                                    </Box>
                                )))
                                    :
                                    (<Typography sx={{ textAlign: 'center', marginTop: '50px' }}>No Projects Available</Typography>)
                            }
                        </Box>
                    </Box>)
                }

            </Box>
        </Box>
    )
}

export default AddIntegrations;
